/**
 * Journey intelligence: a catalog of the product's key journeys + a lightweight tracker that
 * emits start / step / complete / abandon / failure events with duration buckets.
 *
 * Completion is driven by REAL success (route reached, API ok, domain state) — never by a
 * button click or a timer. Abandonment is inferred when the user navigates out of a journey's
 * scope before completing it. See docs/observability/clarity-user-journeys-and-funnels.md.
 */

import { setClarityTags, trackClarityEvent, type ClarityTags } from './clarity';
import { durationBucket } from './events';

export type JourneyId =
  | 'login'
  | 'signup-onboarding'
  | 'first-login-password'
  | 'create-invoice'
  | 'create-contact'
  | 'pos-sale'
  | 'paywall-upgrade'
  | 'session-recovery';

export interface JourneyDef {
  id: JourneyId;
  name: string;
  /** Entry route (where the journey begins). For docs + funnel design. */
  entryRoute: string;
  /** Ordered required steps (ids used with trackJourneyStep). */
  steps: string[];
  /**
   * Optional scope predicate. While a journey is active, navigating to a path for which this
   * returns false marks it abandoned. Omit for journeys ended only by explicit complete/fail.
   */
  inScope?: (pathname: string) => boolean;
}

export const JOURNEYS: Record<JourneyId, JourneyDef> = {
  login: {
    id: 'login',
    name: 'Returning user sign-in',
    entryRoute: '/login',
    steps: ['form-viewed', 'submitted', 'authenticated'],
  },
  'signup-onboarding': {
    id: 'signup-onboarding',
    name: 'Sign up → verify → onboard',
    entryRoute: '/signup',
    steps: ['signup-form', 'verify-code', 'pending-approval'],
    inScope: (p) => p.startsWith('/signup') || p.startsWith('/verify'),
  },
  'first-login-password': {
    id: 'first-login-password',
    name: 'First-login mandatory password change',
    entryRoute: '/change-password',
    steps: ['prompted', 'submitted', 'changed'],
    inScope: (p) => p.startsWith('/change-password'),
  },
  'create-invoice': {
    id: 'create-invoice',
    name: 'Create an invoice',
    entryRoute: '/invoices',
    steps: ['list-viewed', 'form-opened', 'submitted', 'persisted'],
  },
  'create-contact': {
    id: 'create-contact',
    name: 'Create a contact',
    entryRoute: '/contacts',
    steps: ['list-viewed', 'form-opened', 'submitted', 'persisted'],
  },
  'pos-sale': {
    id: 'pos-sale',
    name: 'Complete a POS sale',
    entryRoute: '/pos/terminal',
    steps: ['terminal-opened', 'cart-built', 'checkout', 'sale-completed'],
    inScope: (p) => p.startsWith('/pos'),
  },
  'paywall-upgrade': {
    id: 'paywall-upgrade',
    name: 'Locked module → pricing',
    entryRoute: '/pricing',
    steps: ['paywall-hit', 'pricing-viewed', 'support-contacted'],
  },
  'session-recovery': {
    id: 'session-recovery',
    name: 'Session expiry → re-login',
    entryRoute: '/login',
    steps: ['session-expired', 'login-viewed', 'reauthenticated'],
  },
};

interface ActiveJourney {
  startedAt: number;
  stepStartedAt: number;
  currentStep?: string;
}

const now = (): number =>
  typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();

class JourneyTracker {
  private active = new Map<JourneyId, ActiveJourney>();

  private tagJourney(id: JourneyId, status: string, extra?: ClarityTags): void {
    setClarityTags({ journey_id: id, journey_status: status, ...extra });
  }

  start(id: JourneyId, metadata?: ClarityTags): void {
    this.active.set(id, { startedAt: now(), stepStartedAt: now() });
    this.tagJourney(id, 'started', metadata);
    trackClarityEvent('journey-started');
  }

  step(id: JourneyId, stepId: string, metadata?: ClarityTags): void {
    const j = this.active.get(id);
    const t = now();
    if (j) {
      // Bucket the time spent on the PREVIOUS step (which step took too long).
      if (j.currentStep) {
        setClarityTags({ journey_prev_step: j.currentStep, journey_step_duration: durationBucket(t - j.stepStartedAt) });
        trackClarityEvent('journey-step-completed');
      }
      j.currentStep = stepId;
      j.stepStartedAt = t;
    }
    this.tagJourney(id, 'in-progress', { journey_step: stepId, ...metadata });
    trackClarityEvent('journey-step-viewed');
  }

  complete(id: JourneyId, metadata?: ClarityTags): void {
    const j = this.active.get(id);
    const total = j ? durationBucket(now() - j.startedAt) : undefined;
    this.active.delete(id);
    this.tagJourney(id, 'completed', { journey_total_duration: total, ...metadata });
    trackClarityEvent('journey-completed');
  }

  abandon(id: JourneyId, metadata?: ClarityTags): void {
    if (!this.active.has(id)) return; // already terminal — don't double-fire
    const j = this.active.get(id)!;
    this.active.delete(id);
    this.tagJourney(id, 'abandoned', {
      journey_abandoned_step: j.currentStep,
      journey_total_duration: durationBucket(now() - j.startedAt),
      ...metadata,
    });
    trackClarityEvent('journey-abandoned');
  }

  fail(id: JourneyId, errorContext?: ClarityTags): void {
    const j = this.active.get(id);
    this.active.delete(id);
    this.tagJourney(id, 'failed', { journey_failed_step: j?.currentStep, ...errorContext });
    trackClarityEvent('journey-failed');
  }

  /** Called by the route observer: abandon any in-scope journey the user navigated away from. */
  onRouteChange(pathname: string): void {
    for (const [id] of this.active) {
      const def = JOURNEYS[id];
      if (def.inScope && !def.inScope(pathname)) {
        this.abandon(id, { abandon_reason: 'navigated-away' });
      }
    }
  }
}

export const journeyTracker = new JourneyTracker();

// ── Public API (spec-named wrappers) ─────────────────────────────────────────
export const trackJourneyStart = (id: JourneyId, metadata?: ClarityTags) => journeyTracker.start(id, metadata);
export const trackJourneyStep = (id: JourneyId, stepId: string, metadata?: ClarityTags) =>
  journeyTracker.step(id, stepId, metadata);
export const trackJourneyComplete = (id: JourneyId, metadata?: ClarityTags) => journeyTracker.complete(id, metadata);
export const trackJourneyAbandon = (id: JourneyId, metadata?: ClarityTags) => journeyTracker.abandon(id, metadata);
export const trackJourneyFailure = (id: JourneyId, errorContext?: ClarityTags) => journeyTracker.fail(id, errorContext);
